import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Stack from '@mui/material/Stack';
import MuiToolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PinDropRoundedIcon from '@mui/icons-material/PinDropRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import Tooltip from '@mui/material/Tooltip';
import { useAuth } from '../../context/AuthContext';
import { kSupportedLanguages, SupportedLanguage } from '../../types/Auth';



const StyledAppBar = styled(AppBar)(({ theme }) => `
    background-color: ${theme.palette.background.paper};
    background-image: none;
    border-bottom: 1px solid ${theme.palette.divider};
    box-shadow: none;
    top: var(--template-frame-height, 0px);
`);

const Toolbar = styled(MuiToolbar)(({ theme }) => `
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1.5)};
    padding: ${theme.spacing(1, 1.5)};
    width: 100%;
`);

const LocationButton = styled(ButtonBase)(({ theme }) => `
    align-items: center;
    border-radius: ${theme.shape.borderRadius}px;
    display: flex;
    flex-shrink: 1;
    gap: ${theme.spacing(0.5)};
    min-width: 0;
    overflow: hidden;
    padding: ${theme.spacing(0.5, 1)};
    &:hover {
        background-color: ${theme.palette.action.hover};
    }
`);

export default function AppNavbar() {
    const [locationMenuOpen, setLocationMenuOpen] = useState(false);
    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
    const [locationAnchorEl, setLocationAnchorEl] = useState<null | HTMLElement>(null);
    const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);

    const { t } = useTranslation('common');
    const { locations, language: currentLanguage, activeLocation, changeActiveLocation, setLanguage, doSignOut } = useAuth();
    const hasMultipleLocations = locations.length > 1;

    const handleLocationClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!hasMultipleLocations) return;
        setLocationAnchorEl(event.currentTarget);
        setLocationMenuOpen(true);
    };

    const handleLocationSelect = (locationId: number) => {
        setLocationMenuOpen(false);
        if (locationId !== activeLocation?.id) {
            changeActiveLocation(locationId);
        }
    };

    const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
        setLanguageAnchorEl(event.currentTarget);
        setLanguageMenuOpen(true);
    };

    const handleLanguageSelect = (lang: SupportedLanguage) => {
        setLanguageMenuOpen(false);
        if (lang !== currentLanguage) {
            setLanguage(lang);
        }
    };

    return (
        <StyledAppBar position="fixed">
            <Toolbar variant="regular">
                <img src="/logo.svg" alt="Logo" style={{ maxHeight: 18 }} />

                <Stack direction="row" sx={{ alignItems: 'center', flexShrink: 1, gap: 1, minWidth: 0, ml: 'auto', overflow: 'hidden' }}>
                    {activeLocation?.name && (
                        <>
                            <Tooltip title={hasMultipleLocations ? t('navbar.changeLocation') : ''}>
                                <LocationButton onClick={handleLocationClick} disableRipple={!hasMultipleLocations}>
                                    <PinDropRoundedIcon sx={{ fontSize: '0.875rem' }} />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {activeLocation.name}
                                    </Typography>
                                    {hasMultipleLocations && (
                                        <KeyboardArrowDownRoundedIcon sx={{ fontSize: '1.1rem' }} />
                                    )}
                                </LocationButton>
                            </Tooltip>
                            <Menu
                                anchorEl={locationAnchorEl}
                                open={locationMenuOpen}
                                onClose={() => setLocationMenuOpen(false)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                {locations.map(loc => (
                                    <MenuItem
                                        key={loc.id}
                                        selected={loc.id === activeLocation.id}
                                        onClick={() => handleLocationSelect(loc.id)}
                                    >
                                        {loc.name}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}

                    <Tooltip title={t('navbar.changeLanguage')}>
                        <LocationButton onClick={handleLanguageClick}>
                            <Typography variant="body2">
                                {currentLanguage.toUpperCase()}
                            </Typography>
                        </LocationButton>
                    </Tooltip>
                    <Menu
                        anchorEl={languageAnchorEl}
                        open={languageMenuOpen}
                        onClose={() => setLanguageMenuOpen(false)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        {kSupportedLanguages.map(lang => (
                            <MenuItem
                                key={lang}
                                selected={lang === currentLanguage}
                                onClick={() => handleLanguageSelect(lang)}
                            >
                                {t(`language.${lang}`)}
                            </MenuItem>
                        ))}
                    </Menu>

                    <Tooltip title={t('navbar.logout')}>
                        <LocationButton onClick={doSignOut}>
                            <PowerSettingsNewRoundedIcon sx={{ fontSize: '1.1rem' }} />
                        </LocationButton>
                    </Tooltip>
                </Stack>
            </Toolbar>
        </StyledAppBar>
    );
}
